import { useState, useEffect } from "react";
import { UserPlus, Trash2, Shield, Mail, User, AlertCircle } from "lucide-react";
import { addModerator, removeModerator, getModerators, ModeratorInfo } from "../services/moderatorService";
import Card from "./Card";

interface ModeratorManagementProps {
  instructorId: string;
  tier: string;
}

export default function ModeratorManagement({ instructorId, tier }: ModeratorManagementProps) {
  const [moderators, setModerators] = useState<ModeratorInfo[]>([]);
  const [newModeratorEmail, setNewModeratorEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const canAddModerators = tier === "yearly";
  const isUnlimited = tier === "yearly";

  useEffect(() => {
    loadModerators();
  }, [instructorId]);

  const loadModerators = async () => {
    setLoading(true);
    try {
      const mods = await getModerators(instructorId);
      setModerators(mods);
    } catch (err) {
      console.error("Error loading moderators:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddModerator = async () => {
    if (!newModeratorEmail.trim()) {
      setError("กรุณากรอกอีเมลของผู้ดูแล");
      return;
    }

    if (!canAddModerators) {
      setError("คุณต้องอัปเกรดเป็น Yearly เพื่อเพิ่มผู้ดูแล");
      return;
    }


    setAdding(true);
    setError("");
    setMessage("");

    const result = await addModerator(instructorId, newModeratorEmail);

    if (result.success) {
      setMessage(result.message);
      setNewModeratorEmail("");
      await loadModerators();
    } else {
      setError(result.message);
    }

    setAdding(false);
  };

  const handleRemoveModerator = async (moderatorId: string) => {
    if (!confirm("คุณแน่ใจหรือไม่ว่าต้องการลบผู้ดูแลคนนี้?")) {
      return;
    }

    setError("");
    setMessage("");

    const result = await removeModerator(instructorId, moderatorId);

    if (result.success) {
      setMessage(result.message);
      await loadModerators();
    } else {
      setError(result.message);
    }
  };

  if (!canAddModerators) {
    return (
      <Card className="bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              การจัดการผู้ดูแล (Moderator)
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              ฟีเจอร์นี้สำหรับแพ็กเกจ <strong>Yearly</strong> เท่านั้น
            </p>
            <p className="text-sm text-gray-600">
              <strong>Yearly:</strong> เพิ่มผู้ดูแลได้ไม่จำกัด
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              จัดการผู้ดูแล (Moderator)
            </h2>
            <p className="text-sm text-gray-500">เพิ่มผู้ดูแลเพื่อช่วยจัดการระบบของคุณ</p>
          </div>
        </div>

        {message && (
          <div className="mb-4 p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">{message}</p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 mb-6">
          <label className="block text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            เพิ่มผู้ดูแลใหม่
          </label>
          <p className="text-sm text-gray-600 mb-4">
            กรอกอีเมลของผู้ใช้ที่ต้องการเพิ่มเป็นผู้ดูแล (ผู้ใช้ต้องมีบัญชีในระบบแล้ว)
          </p>
          <div className="flex gap-3">
            <input
              type="email"
              value={newModeratorEmail}
              onChange={(e) => setNewModeratorEmail(e.target.value)}
              placeholder="example@email.com"
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-purple-100 focus:outline-none transition-all"
              disabled={adding}
            />
            <button
              onClick={handleAddModerator}
              disabled={adding || !newModeratorEmail.trim()}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-pink-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {adding ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  กำลังเพิ่ม...
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  เพิ่มผู้ดูแล
                </>
              )}
            </button>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            รายชื่อผู้ดูแล ({moderators.length}{!isUnlimited ? '/5' : ''} คน)
          </h3>
          {!isUnlimited && moderators.length >= 5 && (
            <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
              <p className="text-sm text-blue-800">
                ⚠️ อัปเกรดเป็น Yearly เพื่อเพิ่มผู้ดูแลได้ไม่จำกัด
              </p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">กำลังโหลด...</p>
            </div>
          ) : moderators.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium mb-2">ยังไม่มีผู้ดูแล</p>
              <p className="text-gray-400 text-sm">เพิ่มผู้ดูแลเพื่อช่วยจัดการระบบของคุณ</p>
            </div>
          ) : (
            <div className="space-y-3">
              {moderators.map((mod) => (
                <div
                  key={mod.uid}
                  className="flex items-center justify-between p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-300 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 flex items-center gap-2">
                        {mod.name || "ไม่มีชื่อ"}
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-lg">
                          MODERATOR
                        </span>
                      </p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {mod.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveModerator(mod.uid)}
                    className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-semibold transition-all flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    ลบ
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-2">เกี่ยวกับผู้ดูแล (Moderator)</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>ผู้ดูแลสามารถจัดการคอร์ส นักเรียน และเนื้อหาได้เหมือนผู้สอน</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>ผู้ดูแลไม่สามารถเปลี่ยนแปลงการตั้งค่าแพ็กเกจหรือการชำระเงินได้</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Yearly: เพิ่มได้ไม่จำกัด</span>
              </li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
